import { ThemeEvents } from '@theme/events';

const RESULTS_SELECTOR = '[data-compact-product-recommendations-results]';
const SCROLLER_SELECTOR = '.compact-product-recommendations__scroller';
const SKELETON_SELECTOR = '.compact-product-recommendations__skeleton';
const CARD_SELECTOR = '.compact-product-recommendations__card, .compact-product-recommendations__skeleton-card';

class CompactProductRecommendations extends HTMLElement {
  #intersectionObserver;
  #mutationObserver;
  #activeFetch = null;
  #loadedForUrl = null;
  /** @type {string | null} */
  #cachedHtml = null;
  #handleCartUpdate = (event) => {
    queueMicrotask(() => {
      this.#syncFromCartEvent(event);
    });
  };

  connectedCallback() {
    if (!this.dataset.fetchUrl) return;

    if (this.dataset.syncFromCart === 'true') {
      document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    this.#intersectionObserver.observe(this);

    this.#mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-fetch-url') {
          this.#load();
          continue;
        }

        if (mutation.type === 'childList') {
          this.#restoreOrReloadIfSkeleton();
        }
      }
    });

    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-fetch-url'],
      childList: true,
      subtree: true,
    });

    this.#enableDragScroll(this.querySelector(SKELETON_SELECTOR));
  }

  /**
   * Enables click-and-drag horizontal scrolling on desktop.
   * @param {Element | null} element
   */
  #enableDragScroll(element) {
    if (!(element instanceof HTMLElement) || element.dataset.dragScrollEnabled === 'true') return;

    element.dataset.dragScrollEnabled = 'true';

    let isMouseDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let hasDragged = false;

    const onMouseMove = (event) => {
      if (!isMouseDown) return;

      const delta = event.pageX - startX;

      if (Math.abs(delta) > 5) {
        hasDragged = true;
      }

      if (!hasDragged) return;

      event.preventDefault();
      element.scrollLeft = scrollLeft - delta;
    };

    const onMouseUp = () => {
      if (!isMouseDown) return;

      isMouseDown = false;
      element.classList.remove('is-dragging');
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);

      if (hasDragged) {
        this.#snapToNearest(element);
      }
    };

    element.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;

      isMouseDown = true;
      hasDragged = false;
      startX = event.pageX;
      scrollLeft = element.scrollLeft;
      element.classList.add('is-dragging');

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    });

    element.addEventListener(
      'click',
      (event) => {
        if (!hasDragged) return;

        event.preventDefault();
        event.stopPropagation();
        hasDragged = false;
      },
      true
    );

    element.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });
  }

  /**
   * Smoothly aligns the scroller to the nearest card.
   * @param {HTMLElement} element
   */
  #snapToNearest(element) {
    const cards = element.querySelectorAll(CARD_SELECTOR);

    if (!cards.length) return;

    const maxScroll = element.scrollWidth - element.clientWidth;
    if (maxScroll <= 0) return;

    const { scrollLeft } = element;
    let nearestLeft = 0;
    let minDistance = Infinity;

    for (const card of cards) {
      const cardLeft = card.offsetLeft;
      const distance = Math.abs(cardLeft - scrollLeft);

      if (distance < minDistance) {
        minDistance = distance;
        nearestLeft = cardLeft;
      }
    }

    const distanceToEnd = Math.abs(scrollLeft - maxScroll);
    const targetLeft = distanceToEnd < minDistance ? maxScroll : nearestLeft;

    element.scrollTo({ left: targetLeft, behavior: 'smooth' });
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#intersectionObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#activeFetch?.abort?.();
  }

  /**
   * Cart drawer only: hide/show the whole recommendations block (heading + cards).
   * @param {boolean} visible
   */
  #setCartSectionVisible(visible) {
    const wrapper = this.closest('.cart-drawer__recommendations');
    if (!wrapper) return;

    wrapper.classList.toggle('hidden', !visible);
  }

  /**
   * Rebuilds recommendations when the cart's first product changes.
   * @param {Event} event
   */
  #syncFromCartEvent(event) {
    if (!this.isConnected) return;

    const cart = /** @type {{ items?: Array<{ product_id: number }>, item_count?: number } | undefined} */ (
      event.detail?.resource
    );
    const itemCount = event.detail?.data?.itemCount ?? cart?.item_count ?? 0;

    if (itemCount === 0 || !cart?.items?.length) {
      this.#setCartSectionVisible(false);
      this.classList.add('hidden');
      this.#loadedForUrl = null;
      this.#cachedHtml = null;
      return;
    }

    // Show section (with skeleton) while fetching; #load hides it if empty.
    this.#setCartSectionVisible(true);
    this.classList.remove('hidden');

    const productId = cart.items[0].product_id;
    const nextUrl = this.#buildFetchUrl(productId);
    const urlChanged = this.dataset.fetchUrl !== nextUrl;

    if (urlChanged) {
      this.dataset.fetchUrl = nextUrl;
      // Attribute observer triggers #load when the URL actually changes.
      return;
    }

    // Same product — only recover if morph/skeleton wiped the cards.
    this.#restoreOrReloadIfSkeleton();
  }

  /**
   * If morph replaced cards with the skeleton, restore the last fetch (no flash/refetch).
   */
  #restoreOrReloadIfSkeleton() {
    if (this.classList.contains('is-loading')) return;
    if (!this.querySelector(SKELETON_SELECTOR)) return;

    const url = this.dataset.fetchUrl;
    if (url && this.#cachedHtml && this.#loadedForUrl === url) {
      this.#applyHtml(this.#cachedHtml);
      return;
    }

    this.#load(true);
  }

  /**
   * @param {number} productId
   * @returns {string}
   */
  #buildFetchUrl(productId) {
    const url = new URL(this.dataset.fetchUrl, window.location.origin);
    url.searchParams.set('product_id', String(productId));
    return `${url.pathname}${url.search}`;
  }

  /**
   * @param {string} html
   */
  #applyHtml(html) {
    this.#setCartSectionVisible(true);
    this.classList.remove('hidden');
    this.innerHTML = html;
    this.#enableDragScroll(this.querySelector(SCROLLER_SELECTOR));
  }

  async #load(force = false) {
    const url = this.dataset.fetchUrl;
    if (!url) return;
    if (!force && this.#loadedForUrl === url && this.querySelector(SCROLLER_SELECTOR)) return;

    this.#loadedForUrl = url;
    this.classList.add('is-loading');

    this.#activeFetch?.abort?.();
    this.#activeFetch = new AbortController();

    try {
      const res = await fetch(url, { signal: this.#activeFetch.signal });
      const htmlText = await res.text();

      const doc = new DOMParser().parseFromString(htmlText, 'text/html');
      const resultsRoot = doc.querySelector(RESULTS_SELECTOR);
      const newInner = resultsRoot?.innerHTML?.trim() ?? '';

      this.classList.remove('is-loading');

      if (!newInner.length) {
        this.#cachedHtml = null;
        this.classList.add('hidden');
        // Cart drawer: hide heading + section when recommendations API returns nothing
        this.#setCartSectionVisible(false);
        return;
      }

      this.#cachedHtml = newInner;
      this.#applyHtml(newInner);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;

      console.error('Compact product recommendations error:', e);
      this.classList.remove('is-loading');
      this.classList.add('hidden');
      this.#setCartSectionVisible(false);
    }
  }
}

if (!customElements.get('compact-product-recommendations')) {
  customElements.define('compact-product-recommendations', CompactProductRecommendations);
}
